// The tour screen (owner's picks Q5 A and Q8 B, 2026-10-31), one for every map style, the base interface and phones.
// While a tour plays the page wears body[data-tour], whose value is what the tour shows (`text`, `card` or `none`), and
// css/style.css "The tour screen" sets the style's own buttons, menus, lists, search, seasons and time bar aside. What
// stays: the stop's words (the tour's text look, lazy/tourtext.js, or the style's own caption), the place card while
// the tour shows the card, and the two pieces drawn here:
//   - a thin progress line, one segment per stop, the current one filling over the stop's seconds (the `stop` event's
//     `secs`: its flight and its stay); it holds while the tour is paused, and stands still under reduced motion;
//   - back, pause / play, next and Stop tour, shown by a touch, a click, a key or a mouse move for about 3 s (they stay
//     while the tour is paused, under the mouse, or with the keyboard's focus in them). The first press while they are
//     hidden only shows them: a clear layer over the map takes it, so the press that asks for the buttons does not
//     stop the tour. With them up, a press on the map is the visitor's as before (main.js userStop stops the tour).
// What the tour decides (the owner 2026-10-31): `pins: false` sets the map's location pins aside while it plays
// (body[data-tour-pins="off"]; the visitor's map after it keeps what the view says), `steps: false` the progress line.
// No buttons and no layer for a tour that plays on whatever is touched (`touch: keep`, lobby screens), the kiosk's
// attract loop (its own prompt says "touch") or the editor. Words reach the page through dom.js `html`.
import { html } from '../dom.js';
import { icon } from './icons.js';

const SHOW = 3000; // ms the buttons stay after the last touch, click, key or mouse move

export function tourScreen(c) {
  const { api, T } = c, tours = api.tours, body = document.body;
  const el = Object.assign(document.createElement('div'), { id: 'tour-screen' });
  body.append(el);
  let on = false, last = 0, timer = 0, line = null, ctl = null;
  const paused = () => !!tours.paused?.();
  const can = () => on && !el.classList.contains('keep') && !body.classList.contains('attract') && !c.editing;
  /** The pause button says what a press does; the line holds while paused. */
  function sync() {
    const p = paused(), b = ctl?.querySelector('[data-t="pause"]');
    el.classList.toggle('paused', p);
    if (b) b.ariaLabel = b.title = T(p ? 'tour.play' : 'tour.pause');
  }
  /** A touch, a click, a key or a mouse move: the buttons for 3 s. */
  function arm() {
    if (!can()) return;
    last = performance.now();
    if (!timer) { el.classList.toggle('ui', true); timer = setTimeout(rest, SHOW); }
  }
  function rest() {
    const hold = on && (paused() || !!ctl.querySelector(':focus-visible') || (matchMedia('(hover: hover)').matches && ctl.matches(':hover')));
    const left = SHOW - (performance.now() - last);
    if (on && (hold || left > 40)) { timer = setTimeout(rest, hold ? SHOW : left); return; }
    timer = 0;
    el.classList.toggle('ui', false);
  }
  for (const k of ['pointerdown', 'pointermove', 'keydown', 'wheel']) addEventListener(k, () => on && arm(), { capture: true, passive: true });

  /** The playing tour's own pins and steps (asked at every stop: a playlist moves on to another tour). */
  function own() {
    if (tours.own?.('pins') === false) body.dataset.tourPins = 'off'; else delete body.dataset.tourPins;
    el.classList.toggle('no-steps', tours.own?.('steps') === false);
  }
  // a tour begins: the buttons in the page's language, no buttons for a tour that plays on whatever is touched
  function start() {
    on = true;
    body.dataset.tour = tours.own?.('show') || 'card'; // what the tour shows (its `stop` events say it again)
    el.classList.toggle('keep', tours.own?.('touch') === 'keep');
    const b = (t, ic) => html`<button type="button" data-t="${t}" aria-label="${T(`tour.${t}`)}" title="${T(`tour.${t}`)}">${ic}</button>`;
    el.innerHTML = html`<div class="tour-catch"></div><div class="tour-line" data-st-piece="tour-line" aria-hidden="true"></div>
      <div class="tour-ctl" data-st-piece="tour-buttons" role="group" aria-label="${T('tour.start')}">
        ${b('back', icon('prev'))}${b('pause', html`${icon('pause', 'tc-pause')}${icon('play', 'tc-play')}`)}${b('next', icon('next'))}
        <button type="button" class="tc-stop" data-t="stop">${T('tour.stop')}</button>
      </div>`;
    [, line, ctl] = el.children;
    own();
    ctl.onclick = (e) => {
      const t = e.target.closest('[data-t]')?.dataset.t;
      if (t === 'pause') tours.pause?.(!paused());
      else if (t) tours[t]?.(); // back, next, stop
      sync();
    };
    sync();
  }
  // a stop: its segment fills over its seconds (an unknown length: the segment is marked, it does not fill)
  function stop(e) {
    if (!on) start();
    if (!e?.stop) return;
    body.dataset.tour = e.show || body.dataset.tour;
    own();
    const n = Math.max(1, e.count | 0), i = Math.min(n - 1, Math.max(0, e.index | 0)), secs = Number(e.secs);
    line.style.setProperty('--secs', `${secs > 0 && secs < 36000 ? secs : 0}s`);
    line.innerHTML = html`${Array.from({ length: n }, (_, k) => (k < i ? html`<i class="done"></i>` : k > i ? html`<i></i>` : html`<i class="now">${secs > 0 && secs < 36000 ? html`<b></b>` : ''}</i>`))}`;
    ctl.querySelector('[data-t="back"]').disabled = i < 1;
    sync();
  }
  function end() {
    on = false;
    delete body.dataset.tour;
    delete body.dataset.tourPins;
    el.classList.toggle('no-steps', false);
    clearTimeout(timer); timer = 0;
    el.classList.toggle('ui', false);
    el.textContent = '';
    line = ctl = null;
  }
  tours.on('start', start);
  tours.on('stop', stop);
  tours.on('end', end);
  const now = tours.current?.();
  if (now) stop(now);
}
